import React from "react";
import {
  Box,
  Flex,
  AnyObject,
  Space,
  SpaceTheme,
  ThemeHeaderTitleOptions,
  SpaceThemeNames,
  Text,
  SpacePermission,
} from "@thenounproject/lingo-core";
import styled from "styled-components";

import ThemeEditorAccordion from "./ThemeEditorAccordion";
import ThemeEditorOption from "./ThemeEditorOption";
import ThemeFontPicker from "./ThemeFontPicker";
import ThemeColorPicker from "./ThemeColorPicker";
import ThemeTextArea from "./ThemeTextArea";
import ThemeSelect from "./ThemeSelect";
import ThemeHeaderBackgroundSelector from "./ThemeHeaderBackgroundSelector";

import {
  Font,
  SpaceThemeColorOptions,
  SpaceThemeImageOptions,
  SpaceThemeTextOptions,
  SpaceThemeSelectOptions,
  themeSelectOptions,
  spaceTitleSelectOptions,
  SpaceThemePreviews,
} from "../types";
import ThemeImagePicker from "./ThemeImagePicker";
import { DEFAULT_THEME } from "@contexts/ThemeDataContext";
import useUpsell, { AccessStatus } from "@hooks/useUpsell";

export const Wrapper = styled(Box).attrs({
  borderLeft: "default",
  flex: "240px 1 0",
  background: "white",
  overflow: "auto",
  pb: "80px",
})<{ disabled: boolean }>`
  cursor: ${props => props.disabled && "not-allowed"};
  * {
    pointer-events: ${props => props.disabled && "none"};
    user-select: ${props => props.disabled && "none"};
  }
`;

type Props = {
  space: Space;
  fonts: Font[];
  updateTheme: (data: Partial<SpaceTheme>) => void;
  updateFiles: (data: AnyObject) => void;
  newTheme: SpaceTheme;
  existingTheme: SpaceTheme;
  themeEditorView: SpaceThemePreviews;
  isProcessing: boolean;
};

const ThemeEditorControlPanel: React.FC<Props> = ({
  space,
  fonts,
  newTheme,
  existingTheme,
  updateTheme,
  updateFiles,
  themeEditorView,
  isProcessing,
}) => {
  const { headerTitleStyle } = newTheme;
  const isHomeView = themeEditorView === SpaceThemePreviews.home,
    isGalleryView = themeEditorView === SpaceThemePreviews.gallery;

  const titleLogo = headerTitleStyle === ThemeHeaderTitleOptions.logo;
  const titleSpaceName = headerTitleStyle === ThemeHeaderTitleOptions.name;

  const hasPortalsFeature = space.features.includes("portals");

  const fontAccess = useUpsell(SpacePermission.manageCustomThemeFont),
    themeAccess = useUpsell(SpacePermission.manageCustomThemeAppearance);

  /* Rendering functions */
  function renderKitInPortalText() {
    return (
      <Text font="ui.small" color="grayDarkest" mt="-16px">
        Change to “Kit in Portal” to preview
      </Text>
    );
  }

  function renderSpaceHeaderSection() {
    if (isGalleryView || hasPortalsFeature) return;
    return (
      <ThemeEditorAccordion title="Header" openByDefault>
        <ThemeEditorOption title="Title">
          <ThemeSelect<ThemeHeaderTitleOptions>
            {...{
              newTheme,
              updateTheme,
              themeSelectOption: SpaceThemeSelectOptions.headerTitleStyle,
              options: spaceTitleSelectOptions,
            }}
          />
          <Box mt="m">
            {titleSpaceName && (
              <ThemeColorPicker
                {...{
                  existingTheme: DEFAULT_THEME,
                  newTheme,
                  updateTheme,
                  themeColorOption: SpaceThemeColorOptions.headerTitle,
                }}
              />
            )}
            {titleLogo && (
              <ThemeImagePicker
                {...{
                  existingTheme,
                  newTheme,
                  updateTheme,
                  updateFiles,
                  themeImageOption: SpaceThemeImageOptions.headerLogoImage,
                  sizeRecommendation: "400px wide PNG",
                }}
              />
            )}
          </Box>
        </ThemeEditorOption>
        <ThemeEditorOption title="Background">
          <ThemeHeaderBackgroundSelector
            {...{
              existingTheme,
              newTheme,
              updateTheme,
              updateFiles,
            }}
          />
        </ThemeEditorOption>
        <ThemeEditorOption title="Description">
          <ThemeTextArea
            {...{
              newTheme,
              updateTheme,
              themeTextOption: SpaceThemeTextOptions.description,
            }}
          />
          <Box mt="m">
            <ThemeColorPicker
              {...{
                existingTheme: DEFAULT_THEME,
                newTheme,
                updateTheme,
                themeColorOption: SpaceThemeColorOptions.headerDescription,
              }}
            />
          </Box>
        </ThemeEditorOption>
      </ThemeEditorAccordion>
    );
  }

  function renderKitSettingsSection() {
    if (!hasPortalsFeature && isHomeView) return;
    return (
      <>
        <ThemeEditorAccordion title="Asset Backgrounds" openByDefault>
          {hasPortalsFeature && isHomeView && renderKitInPortalText()}
          <ThemeEditorOption title="Colors">
            <Flex flexDirection="column" gap="4px">
              <ThemeColorPicker
                {...{
                  existingTheme: DEFAULT_THEME,
                  newTheme,
                  updateTheme,
                  themeColorOption: SpaceThemeColorOptions.assetLight,
                }}
              />
              <ThemeColorPicker
                {...{
                  existingTheme: DEFAULT_THEME,
                  newTheme,
                  updateTheme,
                  themeColorOption: SpaceThemeColorOptions.assetDark,
                }}
              />
            </Flex>
          </ThemeEditorOption>
        </ThemeEditorAccordion>
        <ThemeEditorAccordion title="Note Highlighting" openByDefault>
          {hasPortalsFeature && isHomeView && renderKitInPortalText()}
          <ThemeEditorOption title="Colors">
            <Flex flexDirection="column" gap="4px">
              <ThemeColorPicker
                {...{
                  existingTheme: DEFAULT_THEME,
                  newTheme,
                  updateTheme,
                  themeColorOption: SpaceThemeColorOptions.noteInfo,
                }}
              />
              <ThemeColorPicker
                {...{
                  existingTheme: DEFAULT_THEME,
                  newTheme,
                  updateTheme,
                  themeColorOption: SpaceThemeColorOptions.noteWarning,
                }}
              />
              <ThemeColorPicker
                {...{
                  existingTheme: DEFAULT_THEME,
                  newTheme,
                  updateTheme,
                  themeColorOption: SpaceThemeColorOptions.noteSuccess,
                }}
              />
            </Flex>
          </ThemeEditorOption>
        </ThemeEditorAccordion>
      </>
    );
  }

  /* Component render */
  return (
    <Wrapper disabled={isProcessing}>
      <ThemeEditorAccordion title="Global Styles" openByDefault>
        <ThemeEditorOption
          title="Theme"
          upgradeBadge={
            themeAccess.accessStatus === AccessStatus.canAccess
              ? null
              : {
                  openUpgradeModal: themeAccess.openUpgradeModal,
                  featureName: "Custom themes",
                  plural: true,
                }
          }>
          <ThemeSelect<SpaceThemeNames>
            {...{
              newTheme,
              updateTheme,
              themeSelectOption: SpaceThemeSelectOptions.themeName,
              options: themeSelectOptions,
            }}
          />
        </ThemeEditorOption>
        <ThemeEditorOption
          title="Font"
          customizationMessage="to use a custom font"
          upgradeBadge={
            fontAccess.accessStatus === AccessStatus.canAccess
              ? null
              : {
                  openUpgradeModal: fontAccess.openUpgradeModal,
                  featureName: "Custom fonts",
                  plural: true,
                }
          }>
          <ThemeFontPicker {...{ fonts, newTheme, existingTheme, updateTheme }} />
        </ThemeEditorOption>
        <ThemeEditorOption title="Colors">
          <ThemeColorPicker
            {...{
              existingTheme: DEFAULT_THEME,
              newTheme,
              updateTheme,
              themeColorOption: SpaceThemeColorOptions.accent,
            }}
          />
        </ThemeEditorOption>
      </ThemeEditorAccordion>
      {renderSpaceHeaderSection()}
      {renderKitSettingsSection()}
    </Wrapper>
  );
};

export default ThemeEditorControlPanel;
